import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { MarketSidebar } from '../components/layout/MarketSidebar';
import { Loading, Modal, ScoreBadge } from '../components/common';
import { formatCurrency } from '../utils/formatters';
import type { Property, Buyer, BuyerCreateInput, BuyerMatch } from '../types';
import './WholesalerPage.css';

type TabType = 'deals' | 'buyers';

export function WholesalerPage() {
  const [searchParams] = useSearchParams();
  const initialMarket = searchParams.get('market');

  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(() => {
    if (initialMarket) return [initialMarket];
    const saved = localStorage.getItem('selectedMarkets');
    return saved ? JSON.parse(saved) : [];
  });

  const [activeTab, setActiveTab] = useState<TabType>('deals');
  const [properties, setProperties] = useState<Property[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(false);
  const [buyersLoading, setBuyersLoading] = useState(false);

  // Modals
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState<Buyer | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [propertyMatches, setPropertyMatches] = useState<BuyerMatch[]>([]);

  // Load properties
  useEffect(() => {
    const loadProperties = async () => {
      if (selectedMarkets.length === 0) {
        setProperties([]);
        return;
      }

      setLoading(true);
      try {
        const allProps: Property[] = [];
        for (const market of selectedMarkets) {
          const data = await api.getMarketProperties(market);
          const props = (data.properties || []).map((p) => ({
            ...p,
            market,
            dom: p.daysOnMarket ?? p.dom,
            score: p.investmentScore ?? p.score ?? p.finalScore ?? 50,
          }));
          allProps.push(...props);
        }
        setProperties(allProps);
      } catch (err) {
        console.error('Failed to load properties:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, [selectedMarkets]);

  // Load buyers
  useEffect(() => {
    loadBuyers();
  }, []);

  const loadBuyers = async () => {
    setBuyersLoading(true);
    try {
      const data = await api.getBuyers();
      setBuyers(data.buyers);
    } catch (err) {
      console.error('Failed to load buyers:', err);
    } finally {
      setBuyersLoading(false);
    }
  };

  // Filter for distressed properties (wholesale deals)
  const distressedProperties = useMemo(() => {
    return properties
      .filter((p) => {
        // Distress signals
        const highDOM = (p.dom ?? 0) >= 45;
        const priceCut = (p.priceCut ?? 0) >= 10;
        const lowScore = (p.score ?? 50) < 60;

        return highDOM || priceCut || lowScore;
      })
      .sort((a, b) => (b.dom ?? 0) - (a.dom ?? 0));
  }, [properties]);

  // Stats
  const stats = useMemo(() => {
    const totalDeals = distressedProperties.length;
    const avgDOM = totalDeals > 0
      ? distressedProperties.reduce((sum, p) => sum + (p.dom ?? 0), 0) / totalDeals
      : 0;
    const avgPrice = totalDeals > 0
      ? distressedProperties.reduce((sum, p) => sum + p.price, 0) / totalDeals
      : 0;

    return {
      totalDeals,
      avgDOM,
      avgPrice,
      activeBuyers: buyers.filter(b => b.active).length,
    };
  }, [distressedProperties, buyers]);

  // Handle matching property to buyers
  const handleFindBuyers = async (property: Property) => {
    setSelectedProperty(property);
    try {
      const result = await api.matchBuyers({
        price: property.price,
        zip: property.zip,
        property_type: property.propertyType,
      });
      setPropertyMatches(result.matches);
    } catch (err) {
      console.error('Failed to match buyers:', err);
      setPropertyMatches([]);
    }
  };

  return (
    <div className="page-with-sidebar">
      <MarketSidebar selectedMarkets={selectedMarkets} onSelectionChange={setSelectedMarkets} />

      <div className="main-content">
        <div className="container">
          <div className="page-header">
            <div>
              <h1>Wholesaler Dashboard</h1>
              <p>Find distressed deals and match with buyers</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="wholesale-tabs">
            <button
              className={`tab ${activeTab === 'deals' ? 'active' : ''}`}
              onClick={() => setActiveTab('deals')}
            >
              Deal Flow
            </button>
            <button
              className={`tab ${activeTab === 'buyers' ? 'active' : ''}`}
              onClick={() => setActiveTab('buyers')}
            >
              Buyer Database ({buyers.length})
            </button>
          </div>

          {/* Stats Bar */}
          <div className="stats-bar wholesale-stats">
            <div className="stat-item">
              <span className="stat-value">{stats.totalDeals}</span>
              <span className="stat-label">Distressed Deals</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.avgDOM.toFixed(0)}</span>
              <span className="stat-label">Avg DOM</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{formatCurrency(stats.avgPrice)}</span>
              <span className="stat-label">Avg Price</span>
            </div>
            <div className="stat-item highlight">
              <span className="stat-value">{stats.activeBuyers}</span>
              <span className="stat-label">Active Buyers</span>
            </div>
          </div>

          {activeTab === 'deals' ? (
            <>
              {/* Distress Signals Legend */}
              <div className="distress-legend">
                <span><span className="signal dom">DOM 45+</span> High Days on Market</span>
                <span><span className="signal price-cut">Price Cut</span> 10%+ Price Reduction</span>
                <span><span className="signal low-score">Low Score</span> Investment Score &lt; 60</span>
              </div>

              {/* Deals List */}
              {loading ? (
                <Loading message="Loading properties..." />
              ) : distressedProperties.length === 0 ? (
                <div className="no-properties">
                  <h3>No Distressed Properties Found</h3>
                  <p>Select markets from the sidebar to find wholesale deals</p>
                </div>
              ) : (
                <div className="deals-table-container">
                  <table className="deals-table">
                    <thead>
                      <tr>
                        <th>Address</th>
                        <th>Price</th>
                        <th>DOM</th>
                        <th>Price Cut</th>
                        <th>Score</th>
                        <th>Distress</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distressedProperties.map((property, index) => (
                        <DealRow
                          key={`${property.address}-${index}`}
                          property={property}
                          onFindBuyers={() => handleFindBuyers(property)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Buyers Tab */}
              <div className="buyers-header">
                <button className="btn-add-buyer" onClick={() => setShowAddBuyer(true)}>
                  + Add Buyer
                </button>
              </div>

              {buyersLoading ? (
                <Loading message="Loading buyers..." />
              ) : buyers.length === 0 ? (
                <div className="no-properties">
                  <h3>No Buyers Yet</h3>
                  <p>Add buyers and their buy boxes to match with properties</p>
                </div>
              ) : (
                <div className="buyers-table-container">
                  <table className="buyers-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Budget</th>
                        <th>ZIPs</th>
                        <th>Types</th>
                        <th>Condition</th>
                        <th>Capacity</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyers.map((buyer) => (
                        <BuyerRow
                          key={buyer.id}
                          buyer={buyer}
                          onEdit={() => setEditingBuyer(buyer)}
                          onDelete={async () => {
                            if (confirm(`Delete buyer ${buyer.name}?`)) {
                              await api.deleteBuyer(buyer.id);
                              loadBuyers();
                            }
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Buyer Modal */}
      <Modal
        isOpen={showAddBuyer || editingBuyer !== null}
        onClose={() => {
          setShowAddBuyer(false);
          setEditingBuyer(null);
        }}
        title={editingBuyer ? 'Edit Buyer' : 'Add Buyer'}
      >
        <BuyerForm
          buyer={editingBuyer || undefined}
          onSave={async (data) => {
            if (editingBuyer) {
              await api.updateBuyer(editingBuyer.id, data);
            } else {
              await api.createBuyer(data);
            }
            setShowAddBuyer(false);
            setEditingBuyer(null);
            loadBuyers();
          }}
          onCancel={() => {
            setShowAddBuyer(false);
            setEditingBuyer(null);
          }}
        />
      </Modal>

      {/* Property Matches Modal */}
      <Modal
        isOpen={selectedProperty !== null}
        onClose={() => {
          setSelectedProperty(null);
          setPropertyMatches([]);
        }}
        title="Buyer Matches"
      >
        {selectedProperty && (
          <PropertyMatchesView
            property={selectedProperty}
            matches={propertyMatches}
          />
        )}
      </Modal>
    </div>
  );
}

function DealRow({
  property,
  onFindBuyers,
}: {
  property: Property;
  onFindBuyers: () => void;
}) {
  const distressSignals: string[] = [];
  if ((property.dom ?? 0) >= 45) distressSignals.push('dom');
  if ((property.priceCut ?? 0) >= 10) distressSignals.push('price-cut');
  if ((property.score ?? 50) < 60) distressSignals.push('low-score');

  return (
    <tr>
      <td className="col-address">
        <div>{property.address}</div>
        <div className="address-details">
          {property.beds} bd | {property.baths} ba | {property.sqft?.toLocaleString()} sqft
        </div>
      </td>
      <td className="col-price">{formatCurrency(property.price)}</td>
      <td className="col-dom">
        <span className={`dom-badge ${(property.dom ?? 0) >= 60 ? 'high' : (property.dom ?? 0) >= 45 ? 'medium' : ''}`}>
          {property.dom ?? '-'}
        </span>
      </td>
      <td className="col-cut">
        {property.priceCut ? (
          <span className="price-cut-badge">-{property.priceCut}%</span>
        ) : '-'}
      </td>
      <td className="col-score">
        {property.score !== undefined && <ScoreBadge score={property.score} />}
      </td>
      <td className="col-signals">
        {distressSignals.map((signal) => (
          <span key={signal} className={`signal ${signal}`}>
            {signal === 'dom' ? 'DOM' : signal === 'price-cut' ? 'Cut' : 'Low'}
          </span>
        ))}
      </td>
      <td className="col-actions">
        <button className="btn-match" onClick={onFindBuyers}>
          Find Buyers
        </button>
        {property.url && (
          <a
            href={property.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-view"
          >
            View
          </a>
        )}
      </td>
    </tr>
  );
}

function BuyerRow({
  buyer,
  onEdit,
  onDelete,
}: {
  buyer: Buyer;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className={!buyer.active ? 'inactive-row' : ''}>
      <td className="col-name">
        <strong>{buyer.name}</strong>
      </td>
      <td className="col-contact">
        <div>{buyer.email}</div>
        {buyer.phone && <div className="phone">{buyer.phone}</div>}
      </td>
      <td className="col-budget">
        {formatCurrency(buyer.price_min)} - {formatCurrency(buyer.price_max)}
      </td>
      <td className="col-zips">
        {buyer.target_zips.length > 0
          ? buyer.target_zips.slice(0, 3).join(', ') + (buyer.target_zips.length > 3 ? '...' : '')
          : 'Any'}
      </td>
      <td className="col-types">
        {buyer.property_types.join(', ') || 'Any'}
      </td>
      <td className="col-condition">
        {buyer.condition_preference.join(', ') || 'Any'}
      </td>
      <td className="col-capacity">
        {buyer.monthly_capacity}/mo
      </td>
      <td className="col-source">
        {buyer.source}
      </td>
      <td className="col-status">
        <span className={`status-badge ${buyer.active ? 'active' : 'inactive'}`}>
          {buyer.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="col-actions">
        <button onClick={onEdit} className="btn-edit">Edit</button>
        <button onClick={onDelete} className="btn-delete">Del</button>
      </td>
    </tr>
  );
}

function BuyerForm({
  buyer,
  onSave,
  onCancel,
}: {
  buyer?: Buyer;
  onSave: (data: BuyerCreateInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<BuyerCreateInput>({
    name: buyer?.name || '',
    email: buyer?.email || '',
    phone: buyer?.phone || '',
    target_zips: buyer?.target_zips || [],
    price_min: buyer?.price_min || 100000,
    price_max: buyer?.price_max || 300000,
    property_types: buyer?.property_types || ['SFR'],
    condition_preference: buyer?.condition_preference || ['Light Rehab'],
    deal_types: buyer?.deal_types || ['Traditional'],
    monthly_capacity: buyer?.monthly_capacity || 1,
    source: buyer?.source || 'manual',
    notes: buyer?.notes || '',
  });

  const [zipsInput, setZipsInput] = useState(form.target_zips?.join(', ') || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...form,
      target_zips: zipsInput.split(',').map(z => z.trim()).filter(z => z),
    });
  };

  const toggleArrayItem = (field: keyof BuyerCreateInput, value: string) => {
    const current = (form[field] as string[]) || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setForm({ ...form, [field]: updated });
  };

  return (
    <form className="buyer-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <label>
          Name *
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>
        <label>
          Email *
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </label>
      </div>

      <div className="form-row">
        <label>
          Phone
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </label>
        <label>
          Source
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          >
            <option value="manual">Manual Entry</option>
            <option value="InvestorLift">InvestorLift</option>
            <option value="ConnectedInvestors">Connected Investors</option>
            <option value="referral">Referral</option>
          </select>
        </label>
      </div>

      <div className="form-section">
        <h4>Buy Box</h4>

        <div className="form-row">
          <label>
            Min Price
            <input
              type="number"
              value={form.price_min}
              onChange={(e) => setForm({ ...form, price_min: Number(e.target.value) })}
              step={10000}
            />
          </label>
          <label>
            Max Price
            <input
              type="number"
              value={form.price_max}
              onChange={(e) => setForm({ ...form, price_max: Number(e.target.value) })}
              step={10000}
            />
          </label>
          <label>
            Monthly Capacity
            <input
              type="number"
              value={form.monthly_capacity}
              onChange={(e) => setForm({ ...form, monthly_capacity: Number(e.target.value) })}
              min={1}
              max={20}
            />
          </label>
        </div>

        <label>
          Target ZIPs (comma-separated)
          <input
            type="text"
            value={zipsInput}
            onChange={(e) => setZipsInput(e.target.value)}
            placeholder="46218, 46219, 46203"
          />
        </label>

        <div className="checkbox-group">
          <span className="group-label">Property Types</span>
          {['SFR', 'Multi', 'Condo', 'Townhouse'].map(type => (
            <label key={type} className="checkbox">
              <input
                type="checkbox"
                checked={form.property_types?.includes(type)}
                onChange={() => toggleArrayItem('property_types', type)}
              />
              {type}
            </label>
          ))}
        </div>

        <div className="checkbox-group">
          <span className="group-label">Condition Preference</span>
          {['Turnkey', 'Light Rehab', 'Heavy Rehab'].map(cond => (
            <label key={cond} className="checkbox">
              <input
                type="checkbox"
                checked={form.condition_preference?.includes(cond)}
                onChange={() => toggleArrayItem('condition_preference', cond)}
              />
              {cond}
            </label>
          ))}
        </div>

        <div className="checkbox-group">
          <span className="group-label">Deal Types</span>
          {['Traditional', 'Subject-To', 'Creative'].map(deal => (
            <label key={deal} className="checkbox">
              <input
                type="checkbox"
                checked={form.deal_types?.includes(deal)}
                onChange={() => toggleArrayItem('deal_types', deal)}
              />
              {deal}
            </label>
          ))}
        </div>
      </div>

      <label>
        Notes
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
        />
      </label>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
        <button type="submit" className="btn-save">
          {buyer ? 'Update' : 'Add'} Buyer
        </button>
      </div>
    </form>
  );
}

function PropertyMatchesView({
  property,
  matches,
}: {
  property: Property;
  matches: BuyerMatch[];
}) {
  return (
    <div className="matches-view">
      <div className="matches-property">
        <div className="matches-property-header">
          <span className="matches-price">{formatCurrency(property.price)}</span>
          {property.score !== undefined && <ScoreBadge score={property.score} />}
        </div>
        <div className="matches-address">{property.address}</div>
        <div className="matches-details">
          {property.beds} bd | {property.baths} ba | {property.sqft?.toLocaleString()} sqft | {property.dom} DOM
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="no-matches">
          <p>No buyers match this property's criteria.</p>
          <p>Try adding more buyers with matching buy boxes.</p>
        </div>
      ) : (
        <>
          <h4>{matches.length} Matching Buyers</h4>
          <div className="matches-list">
            {matches.map((match) => (
              <div key={match.buyer.id} className="match-item">
                <div className="match-header">
                  <span className="match-name">{match.buyer.name}</span>
                  <span className="match-score">{match.score} pts</span>
                </div>
                <div className="match-contact">
                  {match.buyer.email} {match.buyer.phone && `| ${match.buyer.phone}`}
                </div>
                <div className="match-reasons">
                  {match.reasons.map((reason, i) => (
                    <span key={i} className="reason-tag">{reason}</span>
                  ))}
                </div>
                <div className="match-box">
                  Budget: {formatCurrency(match.buyer.price_min)} - {formatCurrency(match.buyer.price_max)} |
                  Types: {match.buyer.property_types.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
